import { FC, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/app/redux/createAction';
import { RequestStatus } from 'src/shared/api/types';
import { Preloader } from 'src/shared/ui/_loaders/Preloader';
import { ContentNavigation, NavItem } from 'src/shared/ui/_nav/ContentNavigation';
import { actionsRecommendations } from '../../_BLL/slice';
import { Content } from '../Content/Content';
import s from './AIInsightsMC.module.scss';
import { Logo } from 'src/shared/ui/Logo';
import { CardV2 } from 'src/shared/ui/_layout/CardV2';

export const AIInsightsMC: FC = () => {
	// * Selectors
	const recommendations = useAppSelector((state) => state.recommendations.recommendations);
	const status = useAppSelector((state) => state.recommendations.status);

	// * Actions
	const dispatch = useAppDispatch();
	const { getRecommendations } = actionsRecommendations;

	useEffect(() => {
		dispatch(getRecommendations());
	}, []);

	const navItems: NavItem[] = (recommendations?.areas ?? []).map(({ name }, index) => ({
		id: name,
		title: `${index + 1}. ${name}`,
	}));

	useEffect(() => {
		if (navItems.length > 0) {
			setActiveItem(navItems[0]);
		}
	}, [recommendations]);

	const [activeItem, setActiveItem] = useState<NavItem | null>(navItems[0]);

	const activeArea = recommendations?.areas.find((area) => activeItem?.id === area.name);

	const [isLongLoading, setIsLongLoading] = useState<boolean>(false);

	useEffect(() => {
		let timer: NodeJS.Timeout | null = null;
		if (status === RequestStatus.loading) {
			timer = setTimeout(() => setIsLongLoading(true), 4000);
		}

		return () => {
			if (timer) {
				clearTimeout(timer);
			}
		};
	}, [status]);

	if (status === RequestStatus.loading) {
		return (
			<div className={s.loader}>
				<div>
					{isLongLoading
						? 'I am sorry for your wait, but I am doing my best to make the advice/input customized to your company information, checked against Denominator’s database of more than 5 million companies'
						: 'AI is running – please note this may take up to one minute'}
				</div>

				<Preloader />
			</div>
		);
	}

	if (activeItem && activeArea) {
		return (
			<div className={s.container}>
				<div className={s.inner}>
					<div className={s.menu}>
						<Logo />

						<ContentNavigation
							navItems={navItems}
							activeItem={activeItem}
							setActiveItem={setActiveItem}
						/>
					</div>

					<Content area={activeArea} />
				</div>

				<CardV2
					className={s.disclaimer}
					variant="pale_primary"
				>
					<strong>Disclaimer:</strong>
					<br /> All information, advice, and suggestions provided by Denominator are for informational purposes only and are used at the recipient’s own risk. Denominator makes no
					representations or warranties, express or implied, as to the accuracy, completeness, or suitability of such information and does not guarantee any specific outcome or result
					from its use. Denominator shall not be liable for any loss or damage arising from reliance on or use of this information.
				</CardV2>
			</div>
		);
	}

	return null;
};
